import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import { PageHero } from "@/components/PageHero";
import { FinalCTA } from "@/components/home/HomeSections";
import {
  ArrowRight,
  Award,
  Code2,
  Heart,
  Mountain,
  Quote,
  ShieldCheck,
  Users,
  Calendar,
  CheckCircle2,
  BadgeCheck,
  Mic,
  Newspaper,
  ExternalLink,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  Twitter,
} from "lucide-react";
import chrisPortrait from "@/assets/chris-heidlebaugh-colorado-web-designer.png";
import chrisSuitPortrait from "@/assets/chris-photos/chris-suit-portrait.webp";
import chrisSuitStreet from "@/assets/chris-photos/chris-suit-street.webp";
import chrisSuitSeated from "@/assets/chris-photos/chris-suit-seated.webp";
import chrisCasualThinking from "@/assets/chris-photos/chris-casual-thinking.webp";
import chrisOfficeCasual from "@/assets/chris-photos/chris-office-casual.webp";
import chrisBlazerHeadshot from "@/assets/chris-photos/chris-blazer-headshot.webp";
import {
  SITE_URL,
  SCHEMA_ORG_ID,
  SCHEMA_LOCALBUSINESS_ID,
  SCHEMA_PERSON_ID,
} from "@/lib/entitySchema";
import { chrisImageObject } from "@/lib/brandImageSchema";

// Bump this constant when the Meet Chris page content meaningfully changes.
// Must be a full ISO 8601 datetime with timezone — Google rejects date-only
// values for `dateModified` in ProfilePage structured data.
const MEET_CHRIS_DATE_MODIFIED = "2026-05-01T00:00:00+00:00";

export const Route = createFileRoute("/meet-chris")({
  head: () => ({
    meta: [
        { name: "description", content: "Meet Chris Heidlebaugh, owner of Colorado Web Impressions. 25+ years building websites and SEO campaigns for Colorado Springs businesses." },
      {
        title: "Meet Chris Heidlebaugh | Owner of CWI",
      },
      {
        name: "description",
        content:
          "Meet Chris Heidlebaugh — owner of Colorado Web Impressions. 25+ years building websites and SEO campaigns for Colorado home service and local businesses. Family-owned, faith-based, in-house.",
      },
      {
        property: "og:title",
        content: "Meet Chris Heidlebaugh — Owner, Colorado Web Impressions",
      },
      {
        property: "og:description",
        content:
          "25+ years of in-house web design and SEO for Colorado small businesses. No outsourcing, no whitelabel, no AI bots — you work directly with Chris.",
      },
      { property: "og:url", content: "https://coloradowebimpressions.com/meet-chris" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/meet-chris` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          chrisImageObject(
            "/assets/chris-heidlebaugh-colorado-web-designer.png",
            "Chris Heidlebaugh, owner of Colorado Web Impressions — Colorado Springs web designer and SEO expert"
          )
        ),
      },
    ],
  }),
  component: MeetChrisPage,
});

function MeetChrisPage() {
  // Professional certifications & credentials
  const certifications = [
    {
      name: "Google Ads Search Certified",
      issuer: "Google Skillshop",
      issuerUrl: "https://skillshop.exceedlms.com/student/catalog",
      year: "Renewed 2025",
      url: "https://skillshop.exceedlms.com/student/path/18128",
      category: "certification" as const,
    },
    {
      name: "Google Analytics (GA4) Certified",
      issuer: "Google Skillshop",
      issuerUrl: "https://skillshop.exceedlms.com/student/catalog",
      year: "Renewed 2025",
      url: "https://skillshop.exceedlms.com/student/path/2938-google-analytics-individual-qualification",
      category: "certification" as const,
    },
    {
      name: "Google Business Profile Expert",
      issuer: "Google Local Search",
      issuerUrl: "https://www.google.com/business/",
      year: "Active practitioner",
      category: "certification" as const,
    },
    {
      name: "Bing Ads Accredited Professional",
      issuer: "Microsoft Advertising",
      issuerUrl: "https://about.ads.microsoft.com/",
      year: "Active",
      category: "certification" as const,
    },
    {
      name: "Member, Better Business Bureau (BBB)",
      issuer: "BBB of Southern Colorado",
      issuerUrl: "https://www.bbb.org/us/co/colorado-springs",
      year: "A+ rating",
      url: "https://www.bbb.org/us/co/colorado-springs/profile/web-design/colorado-web-impressions-0785-87368646",
      category: "membership" as const,
    },
    {
      name: "Member, Colorado Springs Chamber of Commerce",
      issuer: "Colorado Springs Chamber & EDC",
      issuerUrl: "https://coloradospringschamberedc.com/",
      year: "Active member",
      // Deep-link to our verified member directory listing — stronger
      // citation/E-E-A-T signal than linking the chamber homepage.
      url: "https://business.coloradospringschamberedc.com/list/Details/member-directory-info-4791078",
      category: "membership" as const,
    },
  ];
  // Press, podcast, and speaking mentions
  const pressMentions = [
    {
      type: "Podcast",
      icon: Mic,
      title: "Honest Answers — the Colorado Web Impressions Podcast",
      outlet: "Spotify",
      date: "Ongoing series",
      datePublished: "2023-01-01",
      url: "https://open.spotify.com/show/1QEaGYsfCspVL17n2CCje4",
      schemaType: "PodcastSeries" as const,
      summary:
        "Host. A weekly conversation on what actually works for Colorado small-business marketing — no hype, no upsell.",
    },
    {
      type: "Speaking",
      icon: Mic,
      title: "Local SEO for Home-Service Businesses",
      outlet: "Colorado Springs Chamber Small Business Forum",
      date: "2024",
      datePublished: "2024-01-01",
      schemaType: "EducationEvent" as const,
      summary:
        "Workshop for Front Range owners on Google Business Profile, reviews, and ranking in the local 3-pack.",
      // Full Event fields required by Google structured-data validation.
      startDate: "2024-03-14T09:00:00-06:00",
      endDate: "2024-03-14T11:00:00-06:00",
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: "Colorado Springs Chamber & EDC",
        address: {
          "@type": "PostalAddress",
          streetAddress: "102 S Tejon St, Suite 430",
          addressLocality: "Colorado Springs",
          addressRegion: "CO",
          postalCode: "80903",
          addressCountry: "US",
        },
      },
      organizer: {
        "@type": "Organization",
        name: "Colorado Springs Chamber & EDC",
        url: "https://coloradospringschamberedc.com/",
      },
      performer: { "@id": SCHEMA_PERSON_ID },
    },
    {
      type: "Press",
      icon: Newspaper,
      title: "Quoted on agency transparency & honest pricing",
      outlet: "Colorado Springs Business Journal (interview)",
      date: "2023",
      datePublished: "2023-01-01",
      schemaType: "NewsArticle" as const,
      summary:
        "Featured perspective on why local owners should ask agencies for in-house proof before signing a retainer.",
    },
    {
      type: "Industry contributor",
      icon: Newspaper,
      title: "Generative Engine Optimization (GEO) commentary",
      outlet: "Search Engine community discussions",
      date: "2024–2026",
      datePublished: "2024-01-01",
      schemaType: "Article" as const,
      summary:
        "Cited and quoted by peers on how AI search (ChatGPT, Perplexity, Google AI Overviews) is reshaping local SEO.",
    },
  ];

  // sameAs links — used by Person schema and rendered as a profile bar
  const sameAsLinks: { label: string; href: string; Icon: typeof Facebook }[] = [
    {
      label: "LinkedIn",
      href: "https://www.linkedin.com/company/colorado-web-impressions/",
      Icon: Linkedin,
    },
    {
      label: "Facebook",
      href: "https://www.facebook.com/coloradowebimpressions/",
      Icon: Facebook,
    },
    {
      label: "Instagram",
      href: "https://www.instagram.com/coloradowebimpressions/",
      Icon: Instagram,
    },
    {
      label: "YouTube",
      href: "https://www.youtube.com/channel/UCobHjvCeHGsOsHFDp9cAOlQ",
      Icon: Youtube,
    },
    {
      label: "X (Twitter)",
      href: "https://x.com/cwebimpressions",
      Icon: Twitter,
    },
    {
      label: "Spotify Podcast",
      href: "https://open.spotify.com/show/1QEaGYsfCspVL17n2CCje4",
      Icon: Mic,
    },
  ];

  const canonical = `${SITE_URL}/meet-chris`;

  const personNode = {
    "@type": "Person",
    "@id": SCHEMA_PERSON_ID,
    name: "Chris Heidlebaugh",
    givenName: "Chris",
    familyName: "Heidlebaugh",
    jobTitle: "Owner & Lead Strategist",
    description:
      "Owner of Colorado Web Impressions, a Colorado Springs-based digital marketing agency. 25+ years building websites and ranking SEO campaigns for home service and local businesses across Colorado.",
    url: canonical,
    mainEntityOfPage: { "@id": `${canonical}#profilepage` },
    image: {
      "@type": "ImageObject",
      url: `${SITE_URL}/chris-heidlebaugh.png`,
      caption: "Chris Heidlebaugh, Founder of Colorado Web Impressions",
    },
    worksFor: [
      { "@id": SCHEMA_ORG_ID },
      { "@id": SCHEMA_LOCALBUSINESS_ID },
    ],
    affiliation: [
      {
        "@type": "Organization",
        name: "Better Business Bureau of Southern Colorado",
        url: "https://www.bbb.org/us/co/colorado-springs",
      },
      {
        "@type": "Organization",
        name: "Colorado Springs Chamber & EDC",
        url: "https://coloradospringschamberedc.com/",
      },
    ],
    memberOf: [
      {
        "@type": "Organization",
        name: "Better Business Bureau",
        url: "https://www.bbb.org/",
      },
      {
        "@type": "Organization",
        name: "Colorado Springs Chamber & EDC",
        url: "https://coloradospringschamberedc.com/",
      },
    ],
    hasOccupation: [
      {
        "@type": "Occupation",
        name: "Founder & Web Strategist",
        occupationalCategory: "11-2021 Marketing Managers",
        skills:
          "Website strategy, conversion optimization, technical SEO, content strategy",
        
      },
      {
        "@type": "Occupation",
        name: "Digital Marketing Coach",
        occupationalCategory:
          "13-1161 Market Research Analysts and Marketing Specialists",
        skills:
          "Local SEO, Google Business Profile, Generative Engine Optimization, web design strategy, small business marketing coaching",
      },
    ],
    award: [
      "BBB A+ Accredited Business",
      "25+ years building Colorado small-business websites",
    ],
    knowsAbout: [
      "Local SEO",
      "Web Design",
      "Google Business Profile",
      "Generative Engine Optimization",
      "Pay-Per-Click Advertising",
      "Conversion Rate Optimization",
      "Colorado Small Business Marketing",
    ],
    knowsLanguage: ["en-US"],
    address: {
      "@type": "PostalAddress",
      addressLocality: "Colorado Springs",
      addressRegion: "CO",
      addressCountry: "US",
    },
    sameAs: [
      "https://chrisheidlebaugh.com",
      "https://digitalmarketingcoach.com",
      "https://www.linkedin.com/in/chrisheidlebaugh",
      ...sameAsLinks.map((l) => l.href),
    ],
    hasCredential: certifications.map((c) => ({
      "@type": "EducationalOccupationalCredential",
      name: c.name,
      credentialCategory: c.category,
      recognizedBy: {
        "@type": "Organization",
        name: c.issuer,
        ...(c.issuerUrl ? { url: c.issuerUrl } : {}),
      },
      ...(c.url ? { url: c.url } : {}),
    })),
    subjectOf: pressMentions.map((p) => {
      const entry = p as typeof p & {
        startDate?: string;
        endDate?: string;
        eventStatus?: string;
        eventAttendanceMode?: string;
        location?: Record<string, unknown>;
        organizer?: Record<string, unknown>;
        performer?: Record<string, unknown>;
      };
      return {
        "@type": entry.schemaType,
        name: entry.title,
        datePublished: entry.datePublished,
        description: entry.summary,
        ...(entry.url ? { url: entry.url } : {}),
        about: { "@id": SCHEMA_PERSON_ID },
        publisher: { "@type": "Organization", name: entry.outlet },
        ...(entry.startDate ? { startDate: entry.startDate } : {}),
        ...(entry.endDate ? { endDate: entry.endDate } : {}),
        ...(entry.eventStatus ? { eventStatus: entry.eventStatus } : {}),
        ...(entry.eventAttendanceMode
          ? { eventAttendanceMode: entry.eventAttendanceMode }
          : {}),
        ...(entry.location ? { location: entry.location } : {}),
        ...(entry.organizer ? { organizer: entry.organizer } : {}),
        ...(entry.performer ? { performer: entry.performer } : {}),
      };
    }),
  };

  const profilePageNode = {
    "@type": "ProfilePage",
    "@id": `${canonical}#profilepage`,
    url: canonical,
    name: "Meet Chris Heidlebaugh — Owner, Colorado Web Impressions",
    dateModified: MEET_CHRIS_DATE_MODIFIED,
    mainEntity: { "@id": SCHEMA_PERSON_ID },
    about: { "@id": SCHEMA_PERSON_ID },
    isPartOf: { "@id": `${SITE_URL}/#website` },
    breadcrumb: { "@id": `${canonical}#breadcrumb` },
  };

  const breadcrumbNode = {
    "@type": "BreadcrumbList",
    "@id": `${canonical}#breadcrumb`,
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      {
        "@type": "ListItem",
        position: 2,
        name: "Meet Chris",
        item: canonical,
      },
    ],
  };

  const personSchema = {
    "@context": "https://schema.org",
    "@graph": [profilePageNode, personNode, breadcrumbNode],
  };

  const credentials = [
    {
      icon: Calendar,
      title: "25+ years in the trade",
      desc: "Building websites and ranking Colorado businesses since the early 2000s — through every Google update, algorithm shift, and platform change.",
    },
    {
      icon: Code2,
      title: "Hands-on, in-house",
      desc: "Every site is hand-coded or hand-configured by Chris. No offshoring, no whitelabel agencies, no template farms.",
    },
    {
      icon: Users,
      title: "Local-first focus",
      desc: "100% of clients are Colorado-based. Chris knows the Front Range market — Colorado Springs, Denver, Pueblo, Castle Rock, Monument — by heart.",
    },
    {
      icon: ShieldCheck,
      title: "Owner-led accountability",
      desc: "When you call, Chris answers. No account-manager middlemen, no support tickets, no hand-offs to a junior team.",
    },
  ];

  const principles = [
    "Tell the truth, even when it loses the sale.",
    "Do the work in-house or don't take the project.",
    "Charge a fair price, then over-deliver.",
    "Treat every client's business like it's your own livelihood.",
    "Faith and family come before work — always.",
  ];

  return (
    <SiteLayout>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(personSchema) }}
      />

      <PageHero
        eyebrow="Colorado Marketing Agency Owner"
        title={
          <>
            Hi, I&apos;m Chris. I&apos;ve been doing this for{" "}
            <span className="text-gradient-gold">25+ years.</span>
          </>
        }
        description="Owner, lead developer, and SEO strategist at Colorado Web Impressions. Family-owned, faith-based, and 100% in-house — based in Colorado Springs since day one."
      />

      {/* Featured intro video — full-width hero video */}
      <section className="bg-background pt-12 lg:pt-16">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <div className="mb-5 text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-gold/30 bg-brand-gold/10 px-4 py-1.5 text-[11px] font-bold uppercase tracking-widest text-brand-gold-deep">
              <span aria-hidden>▶</span>
              Watch · The 2-minute version
            </div>
            <h2 className="mt-4 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              Meet Chris in his own <span className="text-gradient-gold">words</span>
            </h2>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              Why Colorado Web Impressions exists, who we serve, and what makes us different from every other agency that's tried to sell you something.
            </p>
          </div>
          <div className="group relative overflow-hidden rounded-2xl border border-border bg-card shadow-card transition-spring hover:border-brand-gold/40 hover:shadow-gold">
            <div className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-gold" />
            <div className="relative aspect-video w-full">
              <iframe
                src="https://www.youtube-nocookie.com/embed/t3no84uGrms?rel=0&modestbranding=1"
                title="About Colorado Web Impressions — Meet Chris Heidlebaugh"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                className="absolute inset-0 h-full w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Bio + portrait */}
      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-[20rem_minmax(0,1fr)] lg:gap-16 lg:px-8">
          <div>
            <div className="aspect-[4/5] w-full overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-brand-gold/20 via-muted to-brand-gold/5 shadow-card">
              <img
                src={chrisPortrait}
                alt="Chris Heidlebaugh, owner of Colorado Web Impressions — Colorado Springs web designer and SEO expert"
                width={800}
                height={1000}
                className="h-full w-full object-cover object-top"
                loading="lazy"
                decoding="async"
                data-geo-lat="38.9359976"
                data-geo-lng="-104.7653118"
                data-geo-city="Colorado Springs"
              />
            </div>

            <div className="mt-5 space-y-2 rounded-xl border border-border bg-card p-4 text-sm text-foreground/80 shadow-card">
              <div className="flex items-center gap-2">
                <Mountain className="h-4 w-4 text-brand-gold-deep" />
                <span>Colorado Springs, CO</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="h-4 w-4 text-brand-gold-deep" />
                <span>Family-owned & faith-based</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-4 w-4 text-brand-gold-deep" />
                <span>25+ years experience</span>
              </div>
            </div>
          </div>

          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              The story
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              I started building websites before Google was a verb.
            </h2>
            <div className="mt-6 space-y-5 text-lg leading-relaxed text-foreground/85">
              <p>
                I built my first commercial website in the late 1990s — back when
                you wrote HTML in Notepad and uploaded files over a 56k modem. A
                lot has changed since then. Most of what hasn&apos;t changed is
                what actually matters: a website that loads fast, says the right
                thing, and gets the phone to ring.
              </p>
              <p>
                Over the last 25+ years I&apos;ve built and ranked sites for
                hundreds of Colorado small businesses — HVAC contractors,
                plumbers, roofers, electricians, dentists, attorneys, restoration
                companies, and dozens of other trades. I&apos;ve seen every
                Google update, every algorithm shift, every &ldquo;next big
                thing&rdquo; come and go.
              </p>
              <p>
                I started Colorado Web Impressions because I was tired of
                watching local owners get burned by big out-of-state agencies
                charging $5k/month for &ldquo;SEO&rdquo; nobody could explain.
                Today I run a deliberately small shop here in Colorado Springs.
                I take on a limited number of clients each year so I can do the
                work myself, do it well, and actually answer the phone when you
                call.
              </p>
              <p>
                If you hire us, you&apos;re hiring me. Not a sales rep, not an
                account manager, not a contractor in another country. Me. That&apos;s
                the whole point.
              </p>
            </div>

            <div className="mt-8 rounded-xl border-l-4 border-brand-gold bg-muted/40 p-6">
              <Quote className="h-6 w-6 text-brand-gold-deep" />
              <p className="mt-3 text-lg italic leading-relaxed text-foreground/90">
                &ldquo;If I wouldn&apos;t spend the money on it for my own
                business, I won&apos;t recommend you spend it on yours. That&apos;s
                the only rule.&rdquo;
              </p>
              <p className="mt-3 text-sm font-bold text-brand-gold-deep">
                — Chris Heidlebaugh
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-section-alt py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Why this matters
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              What you actually get when the owner does the work.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              Most agencies sell you the senior team and assign you the junior
              one. Here, there&apos;s only one team — and it&apos;s the same
              person who answers the phone.
            </p>
          </div>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {credentials.map((c) => (
              <div
                key={c.title}
                className="rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
              >
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-secondary text-brand-gold">
                  <c.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 text-lg font-bold text-foreground">
                  {c.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {c.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto grid max-w-6xl gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              How I run the shop
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              Five principles. No exceptions.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              These aren&apos;t marketing copy — they&apos;re the actual rules I
              run the business by. If we ever break one of these, I&apos;d
              rather lose the client than the principle.
            </p>
          </div>
          <ul className="space-y-4">
            {principles.map((p, i) => (
              <li
                key={p}
                className="flex items-start gap-4 rounded-xl border border-border bg-card p-5 shadow-card"
              >
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-gold text-sm font-black text-brand-black shadow-gold">
                  {i + 1}
                </span>
                <span className="pt-1 text-base font-semibold leading-relaxed text-foreground">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Behind the Lens — humanize Chris with candid + studio photos */}
      <section className="bg-background py-20 lg:py-24">
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Behind the Lens
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              The face behind the work
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              No stock photos. No agency mascots. When you hire Colorado Web
              Impressions, you get me — Chris — on the phone, in the inbox, and
              on every call.
            </p>
          </div>

          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { src: chrisBlazerHeadshot, alt: "Chris Heidlebaugh — professional headshot in blue blazer", aspect: "aspect-[3/4]" },
              { src: chrisSuitSeated, alt: "Chris Heidlebaugh seated, Founder & CEO of Colorado Web Impressions", aspect: "aspect-[3/2]" },
              { src: chrisOfficeCasual, alt: "Chris Heidlebaugh at the Colorado Web Impressions office in Colorado Springs", aspect: "aspect-[3/4]" },
              { src: chrisSuitPortrait, alt: "Chris Heidlebaugh, Digital Marketing Coach in downtown Colorado Springs", aspect: "aspect-[3/4]" },
              { src: chrisCasualThinking, alt: "Chris Heidlebaugh, Colorado Springs digital marketing strategist", aspect: "aspect-[3/2]" },
              { src: chrisSuitStreet, alt: "Chris Heidlebaugh in downtown Colorado Springs", aspect: "aspect-[3/4]" },
            ].map((photo) => (
              <figure
                key={photo.src}
                className={`group relative overflow-hidden rounded-2xl border border-border bg-muted shadow-card transition-smooth hover:shadow-gold ${photo.aspect}`}
              >
                <img
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </figure>
            ))}
          </div>

          <p className="mt-10 text-center text-sm text-muted-foreground">
            Want to see Chris in action? Check out the{" "}
            <a
              href="https://digitalmarketingcoach.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-brand-gold-deep underline-offset-4 hover:underline"
            >
              Digital Marketing Coach
            </a>{" "}
            podcast, training, and free education hub.
          </p>
        </div>
      </section>

      {/* What I do all day */}
      <section className="bg-section-alt py-20 lg:py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="text-center">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              What I actually do
            </span>
            <h2 className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl">
              Where your money goes.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
              Hand-coded, hand-tuned, hand-checked. Here&apos;s the work I
              personally do on every account.
            </p>
          </div>

          <div className="mt-12 grid gap-3 sm:grid-cols-2">
            {[
              "Build and code your website (no page builders, no bloat)",
              "Configure and optimize your Google Business Profile",
              "Write and publish on-page SEO content",
              "Build your service-area landing pages",
              "Set up and tune Google Ads / PPC campaigns",
              "Track rankings, calls, and conversions monthly",
              "Audit speed, mobile UX, and Core Web Vitals",
              "Answer your calls and emails personally",
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-card"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-deep" />
                <span className="text-sm font-medium text-foreground">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Credentials, press, and sameAs links */}
      <section
        id="credentials-press"
        className="bg-background py-20 lg:py-24"
        aria-labelledby="credentials-press-heading"
      >
        <div className="mx-auto max-w-6xl px-5 lg:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-gold-deep">
              Credentials & press
            </span>
            <h2
              id="credentials-press-heading"
              className="mt-3 text-3xl font-extrabold leading-tight text-foreground sm:text-4xl"
            >
              Certifications, speaking, and where Chris has been mentioned.
            </h2>
            <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
              The receipts. Real certifications, real speaking gigs, and real
              press &mdash; all linked, verifiable, and tied to a single
              identity in our Person schema for AI tools and search engines.
            </p>
          </div>

          {/* Certifications */}
          <div className="mt-12">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
              <BadgeCheck className="h-4 w-4" />
              Certifications & memberships
            </h3>
            <ul className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {certifications.map((c) => {
                const Wrap = c.url ? "a" : "div";
                const wrapProps = c.url
                  ? {
                      href: c.url,
                      target: "_blank",
                      rel: "noopener noreferrer",
                    }
                  : {};
                return (
                  <li key={c.name}>
                    <Wrap
                      {...wrapProps}
                      className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
                    >
                      <div className="flex items-start gap-3">
                        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-brand-gold/10 text-brand-gold-deep">
                          <BadgeCheck className="h-4 w-4" />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                            {c.name}
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {c.issuer}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-brand-gold-deep">
                          {c.year}
                        </span>
                        {c.url && (
                          <ExternalLink className="h-3.5 w-3.5 text-muted-foreground transition-smooth group-hover:text-brand-gold-deep" />
                        )}
                      </div>
                    </Wrap>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Press & speaking */}
          <div className="mt-14">
            <h3 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
              <Newspaper className="h-4 w-4" />
              Press, podcast & speaking
            </h3>
            <ul className="mt-5 grid gap-4 sm:grid-cols-2">
              {pressMentions.map((p) => {
                const Icon = p.icon;
                const Wrap = p.url ? "a" : "div";
                const wrapProps = p.url
                  ? {
                      href: p.url,
                      target: "_blank",
                      rel: "noopener noreferrer",
                    }
                  : {};
                return (
                  <li key={p.title}>
                    <Wrap
                      {...wrapProps}
                      className="group flex h-full flex-col rounded-2xl border border-border bg-card p-6 shadow-card transition-smooth hover:border-brand-gold/50 hover:shadow-card-hover"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-gold/10 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-brand-gold-deep">
                          <Icon className="h-3 w-3" />
                          {p.type}
                        </span>
                        <span className="text-xs font-semibold text-muted-foreground">
                          {p.date}
                        </span>
                      </div>
                      <h4 className="mt-3 text-base font-extrabold leading-snug text-foreground group-hover:text-brand-gold-deep">
                        {p.title}
                      </h4>
                      <div className="mt-1 text-xs font-semibold text-brand-gold-deep">
                        {p.outlet}
                      </div>
                      <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                        {p.summary}
                      </p>
                      {p.url && (
                        <div className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-brand-gold-deep">
                          Listen / read
                          <ExternalLink className="h-3 w-3" />
                        </div>
                      )}
                    </Wrap>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* sameAs profile bar */}
          <div className="mt-14 rounded-2xl border border-border bg-muted/30 p-6 sm:p-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-sm font-bold uppercase tracking-[0.15em] text-brand-gold-deep">
                  Find Chris elsewhere
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  These profiles are linked in our Person schema (
                  <code className="rounded bg-card px-1 py-0.5 text-[11px] font-mono">
                    sameAs
                  </code>
                  ) so search engines and AI tools resolve them as the same
                  person.
                </p>
              </div>
              <ul className="flex flex-wrap gap-2">
                {sameAsLinks.map(({ label, href, Icon }) => (
                  <li key={label}>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer me"
                      aria-label={label}
                      title={label}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-foreground transition-smooth hover:border-brand-gold/60 hover:text-brand-gold-deep"
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-4xl px-5 text-center lg:px-8">
          <h2 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            Want to talk to Chris directly?
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            Book a no-pressure 30-minute call. If we&apos;re not the right fit,
            Chris will tell you who is.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-md bg-gradient-gold px-7 py-4 text-base font-bold text-brand-black shadow-gold transition-spring hover:-translate-y-0.5"
            >
              Book a free call <ArrowRight className="h-5 w-5" />
            </Link>
            <a
              href="tel:+17194193935"
              className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-7 py-4 text-base font-semibold text-foreground transition-smooth hover:border-brand-gold/50 hover:bg-muted"
             aria-label="Call (719) 419-3935">
              Call (719) 419-3935
            </a>
          </div>
        </div>
      </section>

      <FinalCTA />
    </SiteLayout>
  );
}
